/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Settings › Model & key, rebuilt on the kit (overhaul step 4). The same GET/PUT /v1/admin/config and POST
 * /v1/admin/config/test as before, the same four saves (model and autonomy, the model catalog, budget and advisory, the
 * key): what changed is how it reads. The budget guardrails stay here because the advisory disclaimer lives with them;
 * they edit the same `budget` as Operations › Caps & limits, which the footer says, and are read and saved through the same
 * helpers: a value that is not a cap (-4, 1,500, letters) is refused under its field and nothing is sent, where it used
 * to be saved as 0, clearing the cap; a blank field (or 0) still clears one; and the save carries the caps the form was
 * built from, so one made from caps another admin has changed since is refused (409) and the fields show the caps in
 * force (#232).
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Bot, Check, Info, KeyRound, Plus, Trash2 } from 'lucide-react';
import { api, MODELS, type BudgetSettings, type ConfigStatus, type ModelCatalogEntry, type ModelProviderKind } from '../lib/api';
import { budgetToForm, capsChangedElsewhere, formToBudgetPatch, type GlobalCapErrors, type GlobalCapForm, type GlobalCapKey } from '../lib/usageCaps';
import { linkClick } from '../shell/router';
import {
  Button, Callout, Checkbox, Field, Grid, Icon, Input, LinkButton, NumberInput, PageHeader, Panel, Select, Skeleton, Stack, StatusChip, Table, Textarea,
  type Column,
} from '../ui';
import { dateTime } from '../ui/format';

/** Editable row for the Models panel: roles are edited as comma-separated text. */
interface ModelRow {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  rolesText: string;
  isDefault: boolean;
  /** The chat tier the entry answers ('' = none: a plain catalog model; the default entry is Balanced). */
  tier: '' | 'fast' | 'high';
  /** The API the id runs on (gyro-svc infers OpenAI for gpt-, chatgpt- and o-series ids saved without one). */
  provider: ModelProviderKind;
}

/** The ids gyro-svc reads as OpenAI's when an entry names no provider (gyro-core inferModelProvider). */
const OPENAI_MODEL_ID = /^(?:gpt-|chatgpt-|o[134](?:-|$))/;
/** The description a new OpenAI row gets when its description is empty (editable): the one line the picker shows. */
const OPENAI_ENTRY_DESCRIPTION = 'Code on files, web search and PPP use Claude.';
const OPENAI_NOT_DEFAULT = 'The default model must be a Claude model: code on files, web search and PPP run on Claude.';

const toRows = (models: ModelCatalogEntry[]): ModelRow[] =>
  models.map((m) => ({ id: m.id, label: m.label, description: m.description ?? '', enabled: m.enabled, rolesText: (m.allowedRoles ?? []).join(', '), isDefault: m.default === true, tier: m.tier ?? '', provider: m.provider ?? (OPENAI_MODEL_ID.test(m.id) ? 'openai' : 'anthropic') }));

const fromRows = (rows: ModelRow[]): ModelCatalogEntry[] =>
  rows.map((r) => {
    const allowedRoles = r.rolesText.split(',').map((s) => s.trim()).filter(Boolean);
    return {
      id: r.id.trim(),
      label: r.label.trim() || r.id.trim(),
      ...(r.description.trim() ? { description: r.description.trim() } : {}),
      enabled: r.enabled,
      ...(allowedRoles.length ? { allowedRoles } : {}),
      ...(r.isDefault ? { default: true } : {}),
      ...(r.tier ? { tier: r.tier } : {}),
      ...(r.provider === 'openai' ? { provider: 'openai' as const } : {}),
    };
  });

/** A catalog row with its position, so the table's cells can patch it. */
type Indexed = ModelRow & { index: number };

const AUTONOMY_WORDS: Record<ConfigStatus['autonomy'], string> = { off: 'Off (advise only)', propose: 'Propose (confirm every change)', auto: 'Auto (low-risk fixes run)' };

/** The built-in rate and burst (gyro-svc usageLimits.ts) a blank field falls back to; the rate field's placeholder says 60. */
const BUILT_IN_RATES = { ratePerMin: 60, burst: 20 };

export function Configuration() {
  const [cfg, setCfg] = useState<ConfigStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState('');
  const [cheapModel, setCheapModel] = useState('');
  const [fallbackModel, setFallbackModel] = useState('');
  const [autonomy, setAutonomy] = useState<'off' | 'propose' | 'auto'>('propose');
  const [pauseAutonomy, setPauseAutonomy] = useState(false);
  // The budget guardrails' caps as typed (burst is not on this screen and stays as loaded), the caps the form was built
  // from (sent with the save, so the service refuses it when another admin has changed them since; not the copy another
  // panel's save refreshes) and a refusal under each field that holds something other than a cap.
  const [budgetForm, setBudgetForm] = useState<GlobalCapForm>(budgetToForm(undefined));
  const [budgetBase, setBudgetBase] = useState<BudgetSettings | undefined>(undefined);
  const [budgetErrors, setBudgetErrors] = useState<GlobalCapErrors>({});
  const budgetFields = useRef<HTMLDivElement>(null);
  const [advisory, setAdvisory] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [modelRows, setModelRows] = useState<ModelRow[]>([]);
  /** "Check id" per catalog row: in flight, then the service's status and sentence. */
  const [checks, setChecks] = useState<Record<number, { busy: boolean; ok?: boolean; message?: string }>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ ok: boolean; message?: string } | null>(null);

  /** The budget guardrails' fields and the caps they are compared with on save, both from `budget` as stored. */
  const resetBudget = (budget: BudgetSettings | undefined) => {
    setBudgetForm(budgetToForm(budget));
    setBudgetBase(budget);
    setBudgetErrors({});
  };
  /** A cap typed: kept as typed, and the field's refusal goes. */
  const typeCap = (key: GlobalCapKey, text: string) => {
    setBudgetForm((form) => ({ ...form, [key]: text }));
    setBudgetErrors((errors) => {
      if (!(key in errors)) return errors;
      const rest = { ...errors };
      delete rest[key];
      return rest;
    });
  };

  const load = () =>
    api
      .getConfig()
      .then((c) => {
        setCfg(c);
        setModel(c.model);
        setCheapModel(c.cheapModel ?? '');
        setFallbackModel(c.fallbackModel ?? '');
        setAutonomy(c.autonomy);
        setPauseAutonomy(c.pauseAutonomy);
        resetBudget(c.budget);
        setAdvisory(c.advisoryDisclaimer ?? '');
        setModelRows(toRows(c.models ?? []));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));

  useEffect(() => {
    void load();
  }, []);

  // `after`: what a panel does once its save is stored, or refused.
  const save = async (patch: Parameters<typeof api.saveConfig>[0], after: { saved?: (c: ConfigStatus) => void; refused?: (e: unknown) => Promise<void> } = {}) => {
    setSaving(true);
    setSaved(false);
    setTest(null);
    try {
      const c = await api.saveConfig(patch);
      setCfg(c);
      setModel(c.model);
      setAutonomy(c.autonomy);
      setAnthropicKey('');
      setModelRows(toRows(c.models ?? []));
      after.saved?.(c);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      await after.refused?.(e);
    } finally {
      setSaving(false);
    }
  };

  // The caps are read strictly (#232): one that is not a cap is refused under its field, focus moves to it and nothing
  // is sent, the advisory included. Only the caps that changed are sent, each with the value the form was built from;
  // when another admin has changed one since, the service stores nothing (409) and the fields show the caps in force.
  const saveBudget = () => {
    const { patch, expected, errors } = formToBudgetPatch(budgetForm, budgetBase, BUILT_IN_RATES);
    setBudgetErrors(errors);
    if (Object.keys(errors).length) {
      setSaved(false);
      requestAnimationFrame(() => budgetFields.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus());
      return;
    }
    setError(null);
    const caps = Object.keys(patch).length ? { budget: patch, expectedGlobalCaps: expected } : {};
    void save({ ...caps, advisoryDisclaimer: advisory || null }, {
      saved: (c) => resetBudget(c.budget),
      refused: async (e) => {
        if (!capsChangedElsewhere(e, 'global_caps_changed')) return;
        await api.getConfig().then((c) => { setCfg(c); resetBudget(c.budget); }).catch(() => undefined);
      },
    });
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      setTest(await api.testConfig());
    } finally {
      setTesting(false);
    }
  };

  // ---- Model catalog editing ----
  const patchRow = (i: number, patch: Partial<ModelRow>) => setModelRows((rows) => rows.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  const setDefaultRow = (i: number) => setModelRows((rows) => rows.map((r, ri) => ({ ...r, isDefault: ri === i })));
  const addRow = () => setModelRows((rows) => [...rows, { id: '', label: '', description: '', enabled: true, rolesText: '', isDefault: rows.length === 0, tier: '', provider: 'anthropic' }]);
  const removeRow = (i: number) => { setModelRows((rows) => rows.filter((_, ri) => ri !== i)); setChecks({}); };
  // An OpenAI row takes no chat tier and is never the default (the service refuses both); the id's empty description
  // gets the picker's one line about what stays on Claude.
  const setProvider = (i: number, provider: ModelProviderKind) => setModelRows((rows) => rows.map((r, ri) => (ri !== i ? r : provider === 'openai'
    ? { ...r, provider, tier: '', isDefault: false, description: r.description.trim() ? r.description : OPENAI_ENTRY_DESCRIPTION }
    : { ...r, provider })));
  const checkRow = async (i: number) => {
    const row = modelRows[i];
    if (!row?.id.trim()) return;
    setChecks((c) => ({ ...c, [i]: { busy: true } }));
    try {
      const result = await api.checkModel(row.id.trim(), row.provider);
      setChecks((c) => ({ ...c, [i]: { busy: false, ok: result.ok, message: result.message } }));
    } catch (e) {
      setChecks((c) => ({ ...c, [i]: { busy: false, ok: false, message: e instanceof Error ? e.message : 'The check failed.' } }));
    }
  };
  const openAiDefault = modelRows.some((r) => r.isDefault && r.provider === 'openai');
  const modelRowsInvalid = modelRows.length === 0 || modelRows.some((r) => !r.id.trim()) || new Set(modelRows.map((r) => r.id.trim())).size !== modelRows.length || openAiDefault;

  const catalogColumns: Array<Column<Indexed>> = [
    { key: 'id', header: 'Model id', width: '22%', cell: (r) => <Input size="sm" className="is-mono" placeholder="claude-…" value={r.id} onChange={(e) => patchRow(r.index, { id: e.target.value })} aria-label="Model id" /> },
    { key: 'label', header: 'Label', width: '16%', cell: (r) => <Input size="sm" placeholder={r.id || 'Label'} value={r.label} onChange={(e) => patchRow(r.index, { label: e.target.value })} aria-label="Label" /> },
    // The API the id runs on (OpenAI models in the picker, owner 2026-10-22). The service infers it from the id when a row is saved without one.
    { key: 'provider', header: 'Provider', width: '110px', cell: (r) => <Select size="sm" value={r.provider} onChange={(e) => setProvider(r.index, e.target.value === 'openai' ? 'openai' : 'anthropic')} aria-label={`Provider of ${r.id || 'model'}`}><option value="anthropic">Anthropic</option><option value="openai">OpenAI</option></Select> },
    { key: 'description', header: 'Description', cell: (r) => <Input size="sm" placeholder="Shown as a hint in the picker" value={r.description} onChange={(e) => patchRow(r.index, { description: e.target.value })} aria-label="Description" /> },
    { key: 'enabled', header: 'Enabled', align: 'center', width: '80px', cell: (r) => <input type="checkbox" checked={r.enabled} onChange={(e) => patchRow(r.index, { enabled: e.target.checked })} aria-label={`Enable ${r.id || 'model'}`} /> },
    { key: 'default', header: 'Default', align: 'center', width: '80px', cell: (r) => <input type="radio" name="model-default" checked={r.isDefault} disabled={r.provider === 'openai'} title={r.provider === 'openai' ? OPENAI_NOT_DEFAULT : undefined} onChange={() => setDefaultRow(r.index)} aria-label={`Make ${r.id || 'model'} the default`} /> },
    // The chat's Fast | Balanced | High control: the default entry is Balanced; High is picked inside a chat only, is open to every signed-in user and resets with every new chat.
    { key: 'tier', header: 'Chat tier', width: '110px', cell: (r) => <Select size="sm" value={r.tier} disabled={r.provider === 'openai'} title={r.provider === 'openai' ? 'The Fast and High chat tiers are Claude models; a GPT entry is picked by name.' : undefined} onChange={(e) => patchRow(r.index, { tier: e.target.value as ModelRow['tier'] })} aria-label={`Chat tier of ${r.id || 'model'}`}><option value="">{r.isDefault ? 'Balanced' : '—'}</option><option value="fast">Fast</option><option value="high">High (per chat)</option></Select> },
    { key: 'roles', header: 'Roles', width: '18%', cell: (r) => <Input size="sm" placeholder="everyone" value={r.rolesText} onChange={(e) => patchRow(r.index, { rolesText: e.target.value })} aria-label="Allowed roles" /> },
    // "Check id": the service asks the provider's API whether the key can use this model id (status and a sentence; never the key).
    { key: 'check', header: 'Check', width: '150px', cell: (r) => <><Button size="sm" busy={checks[r.index]?.busy === true} disabled={!r.id.trim()} onClick={() => void checkRow(r.index)} aria-label={`Check the id ${r.id || 'of this model'}`}>Check id</Button>{checks[r.index]?.message && <span className={checks[r.index]?.ok ? 'ui-cell-sub' : 'ui-cell-sub ui-text-bad'} role="status">{checks[r.index]?.message}</span>}</> },
    { key: 'remove', header: <span className="ui-sr">Remove</span>, align: 'end', width: '56px', cell: (r) => <Button variant="ghost" size="sm" icon={Trash2} title="Remove model" aria-label={`Remove ${r.id || 'model'}`} onClick={() => removeRow(r.index)} /> },
  ];

  const modelOptions = MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>);

  return (
    <>
      <PageHeader
        meta={cfg ? (
          <>
            <StatusChip tone="neutral" plain><span className="is-mono">{cfg.model}</span></StatusChip>
            {cfg.hasAnthropicKey ? <StatusChip tone="ok">Key configured</StatusChip> : <StatusChip tone="warn">No API key</StatusChip>}
            {cfg.pauseAutonomy ? <StatusChip tone="warn">Autonomy paused</StatusChip> : <StatusChip tone="neutral">{AUTONOMY_WORDS[cfg.autonomy]}</StatusChip>}
          </>
        ) : !error ? <Skeleton width="220px" /> : null}
      />
      <div className="ui-page">
        <Stack>
          {error && <Callout tone="bad" role="alert" title="The service refused">{error}</Callout>}
          {saved && <Callout tone="ok" role="status">Saved.</Callout>}

          {!cfg && !error ? (
            <Grid cols={2}>
              <Panel title="Model & autonomy" description="The central brain's model and how far it may act on its own">
                <div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="lg" /></div>
              </Panel>
              <Panel title="Anthropic API key" description="Encrypted at rest and never shown again">
                <div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="md" width="160px" /></div>
              </Panel>
            </Grid>
          ) : cfg && (
            <>
              <Grid cols={2}>
                <Panel
                  title="Model & autonomy"
                  description="The central brain's model and how far it may act on its own"
                  footer={<><Icon icon={Info} size="sm" /><span>Surfaces may override the autonomy; financial, security and destructive actions always need confirmation.</span></>}
                >
                  <div className="ui-form">
                    <Field label="Model">
                      <Select value={model} onChange={(e) => setModel(e.target.value)}>
                        {modelOptions}
                        {model && !MODELS.some((m) => m.id === model) && <option value={model}>{model} (current)</option>}
                      </Select>
                    </Field>
                    <Field label="Default autonomy" help="Surfaces may override this; financial/security/destructive always need confirmation.">
                      <Select value={autonomy} onChange={(e) => setAutonomy(e.target.value as typeof autonomy)}>
                        <option value="off">Off — advise only</option>
                        <option value="propose">Propose — confirm every change (recommended)</option>
                        <option value="auto">Auto — auto-run low-risk reversible fixes</option>
                      </Select>
                    </Field>
                    <Field label="Cheap model (voice / short turns)" help="Routes short/voice turns to a cheaper model to cut cost; tool-heavy/detailed turns use the main model.">
                      <Select value={cheapModel} onChange={(e) => setCheapModel(e.target.value)}>
                        <option value="">(none — always use main model)</option>
                        {modelOptions}
                      </Select>
                    </Field>
                    <Field label="Fallback model (on hard model errors)">
                      <Select value={fallbackModel} onChange={(e) => setFallbackModel(e.target.value)}>
                        <option value="">(none)</option>
                        {modelOptions}
                      </Select>
                    </Field>
                    <Checkbox
                      checked={pauseAutonomy}
                      onChange={(e) => setPauseAutonomy(e.target.checked)}
                      label={<><strong>Pause all autonomy</strong> — force every action through propose→confirm (master kill switch)</>}
                    />
                    <div className="ui-actions">
                      <Button variant="primary" icon={Check} busy={saving} onClick={() => void save({ model, cheapModel: cheapModel || null, fallbackModel: fallbackModel || null, autonomy, pauseAutonomy })}>
                        Save model &amp; autonomy
                      </Button>
                    </div>
                  </div>
                </Panel>

                <Panel
                  title="Anthropic API key"
                  description="Encrypted at rest and never shown again"
                  actions={(
                    <>
                      <StatusChip tone="neutral" plain>key source: {cfg.anthropicKeySource}</StatusChip>
                      {cfg.hasAnthropicKey ? <StatusChip tone="ok">configured</StatusChip> : <StatusChip tone="warn">not set</StatusChip>}
                    </>
                  )}
                >
                  <div className="ui-form">
                    <Field
                      label={<><Icon icon={KeyRound} size="sm" /> API key {cfg.hasAnthropicKey && <span className="ui-quiet">(leave blank to keep)</span>}</>}
                      error={!cfg.encryptionAvailable ? 'Encryption secret (TOKEN_ENC_KEY) missing on the service — set it before saving a key.' : undefined}
                    >
                      <Input type="password" placeholder="sk-ant-…" value={anthropicKey} autoComplete="off" onChange={(e) => setAnthropicKey(e.target.value)} />
                    </Field>
                    <div className="ui-actions">
                      <Button variant="primary" icon={Check} busy={saving} disabled={!anthropicKey} onClick={() => void save({ anthropicKey })}>Save key</Button>
                      <Button icon={Bot} busy={testing} onClick={() => void runTest()}>Test connection</Button>
                      {cfg.anthropicKeySource === 'admin' && <Button variant="ghost" disabled={saving} onClick={() => void save({ anthropicKey: null })}>Clear</Button>}
                    </div>
                    {test && (
                      <Callout tone={test.ok ? 'ok' : 'bad'} role={test.ok ? 'status' : 'alert'}>
                        {test.ok ? 'Key works' : test.message ?? 'failed'}
                      </Callout>
                    )}
                  </div>
                </Panel>

                <Panel
                  title="OpenAI API key"
                  description="For the GPT entries of the model catalog; encrypted at rest and never shown again"
                  actions={(
                    <>
                      <StatusChip tone="neutral" plain>key source: {cfg.openAiKeySource ?? 'none'}</StatusChip>
                      {cfg.hasOpenAiKey ? <StatusChip tone="ok">configured</StatusChip> : <StatusChip tone="neutral">not set</StatusChip>}
                    </>
                  )}
                >
                  <div className="ui-form">
                    <Field
                      label={<><Icon icon={KeyRound} size="sm" /> API key {cfg.hasOpenAiKey && <span className="ui-quiet">(leave blank to keep)</span>}</>}
                      error={!cfg.encryptionAvailable ? 'Encryption secret (TOKEN_ENC_KEY) missing on the service — set it before saving a key.' : undefined}
                      help="Without a key, GPT entries stay hidden from the picker. Use Check id on a catalog row to prove the key sees a model."
                    >
                      <Input type="password" placeholder="sk-…" value={openaiKey} autoComplete="off" onChange={(e) => setOpenaiKey(e.target.value)} />
                    </Field>
                    <div className="ui-actions">
                      <Button variant="primary" icon={Check} busy={saving} disabled={!openaiKey} onClick={() => void save({ openaiKey }, { saved: () => setOpenaiKey('') })}>Save key</Button>
                      {cfg.openAiKeySource === 'admin' && <Button variant="ghost" disabled={saving} onClick={() => void save({ openaiKey: null })}>Clear</Button>}
                    </div>
                  </div>
                </Panel>
              </Grid>

              <Panel
                flush
                title="Model catalog"
                description="The models people can pick in the Gyro widget"
                actions={<Button size="sm" icon={Plus} onClick={addRow}>Add model</Button>}
                footer={(
                  <>
                    <Icon icon={Info} size="sm" />
                    <span>
                      Enabled models show up for everyone unless you restrict them to roles; the <strong>default</strong> is what runs when a user hasn&apos;t picked one.
                      Users only ever see models their roles allow; the service also rejects any other choice. Roles are comma-separated; blank means everyone.
                      The <strong>High</strong> chat tier (claude-fable-5-1) ships disabled: its <strong>Enabled</strong> box is the switch for the tier. Removing that row does not retire it; the service adds it back, disabled, on the next read.
                      An <strong>OpenAI</strong> row (a GPT model id, with the OpenAI key saved) is picked by name: it is never the default and takes no chat tier, and code on files, web search and PPP stay on Claude.
                    </span>
                  </>
                )}
              >
                <Table stacked
                  label="Model catalog"
                  columns={catalogColumns}
                  rows={modelRows.map((r, index) => ({ ...r, index }))}
                  rowKey={(r) => String(r.index)}
                  stickyHeader={false}
                  className="ui-table--inputs"
                />
                <div className="ui-panel-pad ui-panel-pad--top">
                  <div className="ui-actions">
                    <Button variant="primary" icon={Check} busy={saving} disabled={modelRowsInvalid} onClick={() => void save({ models: fromRows(modelRows) })}>Save model catalog</Button>
                    {modelRowsInvalid && <span className="ui-cell-sub">{openAiDefault ? OPENAI_NOT_DEFAULT : 'Every row needs a unique, non-empty model id (and keep at least one model).'}</span>}
                  </div>
                </div>
              </Panel>

              <Grid cols={2}>
                <Panel
                  title="Budget guardrails"
                  description="The fleet's spend caps and the advisory disclaimer"
                  actions={<LinkButton variant="ghost" size="sm" href="/caps" iconAfter={ArrowUpRight} onClick={(event) => linkClick(event)}>Caps &amp; limits</LinkButton>}
                  footer={(
                    <>
                      <Icon icon={Info} size="sm" />
                      <span>Caps bound the total spend across every app together (today UTC, last 30 days). The hard cap pauses Gyro and names the cap; the soft threshold (80%) fires an alert. The rate limit is per app, user and IP. The same <code>budget</code> as Operations › Caps &amp; limits.</span>
                    </>
                  )}
                >
                  <div className="ui-form" ref={budgetFields}>
                    {/* Text, not number: a number input reports '--4', '1,500' or '1e' as blank, which would read as "no cap". */}
                    <Grid cols={2}>
                      <Field label="Daily spend cap (USD)" error={budgetErrors.dailyUsd}>
                        <NumberInput placeholder="no cap" value={budgetForm.dailyUsd} onChange={(e) => typeCap('dailyUsd', e.target.value)} />
                      </Field>
                      <Field label="Monthly spend cap (USD)" error={budgetErrors.monthlyUsd}>
                        <NumberInput placeholder="no cap" value={budgetForm.monthlyUsd} onChange={(e) => typeCap('monthlyUsd', e.target.value)} />
                      </Field>
                    </Grid>
                    <Field label="Rate limit (requests / minute / user)" error={budgetErrors.ratePerMin}>
                      <NumberInput placeholder={String(BUILT_IN_RATES.ratePerMin)} value={budgetForm.ratePerMin} onChange={(e) => typeCap('ratePerMin', e.target.value)} />
                    </Field>
                    <Field label="Advisory disclaimer (sensitive-domain answers)" help="Shown under answers touching payroll, GL, contracts, etc. Leave blank for the built-in default.">
                      <Textarea rows={2} value={advisory} onChange={(e) => setAdvisory(e.target.value)} placeholder="This is informational, not legal/financial advice — verify against the system of record." />
                    </Field>
                    <div className="ui-actions">
                      <Button variant="primary" icon={Check} busy={saving} onClick={saveBudget}>
                        Save budget &amp; advisory
                      </Button>
                    </div>
                  </div>
                </Panel>

                <Panel title="Status" description="What the service holds now">
                  <dl className="ui-facts">
                    <dt>Model</dt><dd><code>{cfg.model}</code></dd>
                    <dt>Autonomy</dt><dd>{cfg.autonomy}</dd>
                    <dt>Anthropic key</dt><dd>{cfg.anthropicKeySource}</dd>
                    <dt>OpenAI key</dt><dd>{cfg.openAiKeySource ?? 'none'}</dd>
                    <dt>Encryption</dt><dd>{cfg.encryptionAvailable ? <StatusChip tone="ok">available</StatusChip> : <StatusChip tone="bad">missing</StatusChip>}</dd>
                    {cfg.updatedBy && <><dt>Last updated by</dt><dd>{cfg.updatedBy}{cfg.updatedAt ? <span className="ui-quiet"> · {dateTime(cfg.updatedAt)}</span> : null}</dd></>}
                  </dl>
                </Panel>
              </Grid>
            </>
          )}
        </Stack>
      </div>
    </>
  );
}
